<template>
  <div
    class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center"
  >
    <div
      class="bg-white p-8 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      style="height: 90vh"
    >
      <h2 class="text-2xl font-bold mb-4">
        {{ isEditing ? "Edit Survey" : "Create New Survey" }}
      </h2>

      <div class="mb-4 border-b border-gray-200">
        <nav class="-mb-px flex" aria-label="Tabs">
          <button
            v-for="tab in ['Survey Details', 'Questions']"
            :key="tab"
            @click="activeTab = tab"
            :class="[
              activeTab === tab
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'w-1/2 py-4 px-1 text-center border-b-2 font-medium text-sm',
            ]"
          >
            {{ tab }}
          </button>
        </nav>
      </div>

      <div class="flex-grow overflow-y-auto">
        <form @submit.prevent="submitSurvey">
          <div v-if="activeTab === 'Survey Details'">
            <div class="mb-4">
              <label for="title" class="block text-sm font-medium text-gray-700"
                ><span style="color:red" class="ml-2">*</span> Title</label
              >
              <input
                type="text"
                id="title"
                v-model="survey.title"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
              <p v-if="errors.title" class="mt-1 text-sm text-red-600">
                {{ errors.title }}
              </p>
            </div>
            <div class="mb-4">
              <label
                for="description"
                class="block text-sm font-medium text-gray-700"
                >Description</label
              >
              <textarea
                id="description"
                v-model="survey.description"
                rows="3"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              ></textarea>
            </div>
            <div class="mb-4">
              <label
                for="surveyMode"
                class="block text-sm font-medium text-gray-700"
                ><span style="color:red" class="ml-2">*</span>Survey Mode</label
              >
              <select
                id="surveyMode"
                v-model="survey.surveyMode"
                required
                :disabled="survey.status == 'Published'"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              >
                <option value="adhoc">Ad Hoc</option>
                <option value="recurring">Recurring</option>
              </select>
              <p v-if="errors.surveyMode" class="mt-1 text-sm text-red-600">
                {{ errors.surveyMode }}
              </p>
            </div>
            <div v-if="survey.surveyMode === 'recurring'" class="mb-4">
              <label
                for="recurringPattern"
                class="block text-sm font-medium text-gray-700"
                ><span style="color:red" class="ml-2">*</span>Recurring Pattern</label
              >
              <select
                id="recurringPattern"
                v-model="survey.recurringPattern"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
              <p
                v-if="errors.recurringPattern"
                class="mt-1 text-sm text-red-600"
              >
                {{ errors.recurringPattern }}
              </p>
            </div>
            <div class="mb-4">
              <label for="type" class="block text-sm font-medium text-gray-700"
                ><span style="color:red" class="ml-2">*</span>Type</label
              >
              <select
                v-if="isSuperAdmin"
                id="type"
                v-model="survey.type"
                :disabled="survey.status == 'Published'"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              >
                <option value="">Select a type</option>
                <option value="University">University</option>
                <option value="School/Department">School/Department</option>
              </select>
              <input
                v-else
                type="text"
                id="type"
                v-model="survey.type"
                disabled
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100"
              />
              <p v-if="errors.type" class="mt-1 text-sm text-red-600">
                {{ errors.type }}
              </p>
            </div>

            <div class="mb-4">
              <label
                for="department"
                class="block text-sm font-medium text-gray-700"
                ><span style="color:red" class="ml-2">*</span>Department</label
              >
              <select
                id="department"
                v-model="survey.department"
                required
                @change="handleDepartmentChange"
                :disabled="survey.status == 'Published'"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              >
                <option value="">Select a department</option>
                <option
                  v-for="dept in availableDepartments"
                  :key="dept"
                  :value="dept"
                >
                  {{ dept }}
                </option>
              </select>
              <p v-if="errors.department" class="mt-1 text-sm text-red-600">
                {{ errors.department }}
              </p>
            </div>

            <div class="mb-4" v-if="survey.type == 'School/Department'">
              <label
                for="targetModule"
                class="block text-sm font-medium text-gray-700"
                >Target Modules</label
              >
              <div class="relative">
                <div class="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="selectAllModules"
                    v-model="selectAllModules"
                    @change="toggleAllModules"
                    :disabled="!survey.department || survey.status == 'Published'"
                    class="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label
                    for="selectAllModules"
                    class="ml-2 block text-sm text-gray-900"
                  >
                    Select All
                  </label>
                </div>
                <div class="flex items-center mb-2">
                  <input
                    type="text"
                    id="title"
                    placeholder="Search..."
                    v-model="search"
                    required
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                </div>

                <div
                  class="max-h-60 overflow-y-auto border border-gray-300 rounded-md p-2"
                >
                  <div
                    v-for="module in filteredModules"
                    :key="module.id"
                    v-if="survey.status != 'Published' || survey.targetModule.includes(module.id)"
                    class="flex items-center mb-2"
                  >
                    <input
                      type="checkbox"
                      :id="'module-' + module.id"
                      :value="module.id"
                      v-model="survey.targetModule"
                      :disabled="!survey.department || survey.status == 'Published'"
                      class="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label
                      :for="'module-' + module.id"
                      class="ml-2 block text-sm text-gray-900"
                    >
                      {{ module.moduleCode }} - {{ module.name }}
                    </label>
                  </div>
                </div>
              </div>
              <p v-if="errors.targetModule" class="mt-1 text-sm text-red-600">
                {{ errors.targetModule }}
              </p>
            </div>

            <div class="mb-4">
              <label
                for="startDate"
                class="block text-sm font-medium text-gray-700"
                >Start Date</label
              >
              <input
                type="datetime-local"
                id="startDate"
                v-model="survey.startDate"
                required
                :min="new Date().toISOString().slice(0, 16)"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
            </div>
            <div class="mb-4">
              <label
                for="endDate"
                class="block text-sm font-medium text-gray-700"
                >End Date</label
              >
              <input
                type="datetime-local"
                id="endDate"
                v-model="survey.endDate"
                required
                :min="new Date().toISOString().slice(0, 16)"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
            </div>
          </div>

          <div v-if="activeTab === 'Questions'">
            <div class="mb-4">
              <label
                for="surveyTemplate"
                class="block text-sm font-medium text-gray-700"
                >Survey Template</label
              >
              <div class="relative" ref="surveyTemplateDropdown">
                <input
                  type="text"
                  v-model="surveyTemplateSearch"
                  @focus="surveyTemplateDropdownActive = true"
                  @click="surveyTemplateDropdownActive = true"
                  placeholder="Search survey templates..."
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
                <div
                  v-if="
                    surveyTemplateDropdownActive &&
                    filteredSurveyTemplates.length > 0
                  "
                  class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                >
                  <ul class="max-h-60 overflow-auto">
                    <li
                      v-for="template in filteredSurveyTemplates"
                      :key="template.id"
                      @click="selectSurveyTemplate(template)"
                      class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {{ template.name }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="mb-4">
              <label
                for="questionTemplate"
                class="block text-sm font-medium text-gray-700"
                >Question Template</label
              >
              <div class="relative" ref="questionTemplateDropdown">
                <input
                  type="text"
                  v-model="questionTemplateSearch"
                  @focus="questionTemplateDropdownActive = true"
                  @click="questionTemplateDropdownActive = true"
                  placeholder="Search question templates..."
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
                <div
                  v-if="
                    questionTemplateDropdownActive &&
                    filteredQuestionTemplates.length > 0
                  "
                  class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                >
                  <ul class="max-h-60 overflow-auto">
                    <li
                      v-for="template in filteredQuestionTemplates"
                      :key="template.id"
                      @click="selectQuestionTemplate(template)"
                      class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {{ template.name }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div
              v-for="(question, index) in survey.questions"
              :key="index"
              class="mb-4 p-4 border border-gray-200 rounded-md"
            >
              <div class="flex justify-between items-center mb-2">
                <h4 class="text-md font-medium">Question {{ index + 1 }}</h4>
                <div class="flex items-center space-x-2">
                  <button
                    @click="moveQuestionUp(index)"
                    type="button"
                    class="text-gray-600 hover:text-gray-800"
                    :disabled="index === 0"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                  <button
                    @click="moveQuestionDown(index)"
                    type="button"
                    class="text-gray-600 hover:text-gray-800"
                    :disabled="index === survey.questions.length - 1"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                  <button
                    @click="removeQuestion(index)"
                    type="button"
                    class="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              </div>
              <input
                v-model="question.text"
                type="text"
                placeholder="Question text"
                class="mb-2 w-full px-3 py-2 border border-gray-300 rounded-md"
              />
<!--              <input
                v-model="question.description"
                type="text"
                placeholder="Question description (optional)"
                class="mb-2 w-full px-3 py-2 border border-gray-300 rounded-md"
              />-->
              <select
                v-model="question.type"
                class="mb-2 w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="single">Single Choice</option>
                <option value="multiple">Multiple Choice</option>
                <option value="rating">Star Rating</option>
                <option value="text">Text</option>
              </select>
              <div
                v-if="
                  question.type === 'single' || question.type === 'multiple'
                "
                class="ml-4"
              >
                <div
                  v-for="(option, optionIndex) in question.options"
                  :key="optionIndex"
                  class="flex items-center mb-2"
                >
                  <input
                    v-model="question.options[optionIndex]"
                    type="text"
                    placeholder="Option text"
                    class="flex-grow px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <button
                    @click="removeOption(question, optionIndex)"
                    type="button"
                    class="ml-2 text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
                <button
                  @click="addOption(question)"
                  type="button"
                  class="text-blue-600 hover:text-blue-800"
                >
                  Add Option
                </button>
              </div>
              <div v-if="question.type === 'rating'" class="ml-4">
                <label class="block mb-2">Max Rating:</label>
                <input
                  v-model.number="question.maxRating"
                  type="number"
                  min="1"
                  max="10"
                  class="w-20 px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div v-if="survey.type === 'School/Department'" class="mb-2">
                <label
                  :for="`question-${index}-option`"
                  class="block text-sm font-medium text-gray-700"
                  ><span style="color:red" class="ml-2">*</span>Option (evaluation for module or module/staff)</label
                >
                <select
                  :id="`question-${index}-option`"
                  v-model="question.option"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                >
                  <option value="">Select an option</option>
                  <option value="module">Module</option>
                  <option value="module/staff">Module/Staff</option>
                </select>
              </div>
              <div
                v-if="survey.type === 'School/Department'"
                class="flex items-center mb-2"
              >
                <input
                  type="checkbox"
                  :id="`question-${index}-mandatory`"
                  v-model="question.isMandatory"
                  class="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label
                  :for="`question-${index}-mandatory`"
                  class="ml-2 block text-sm text-gray-900"
                >
                  Mandatory
                </label>
              </div>
            </div>
            <div class="flex items-center mb-4">
              <select
                v-model="newQuestionType"
                class="mr-2 px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="single">Single Choice</option>
                <option value="multiple">Multiple Choice</option>
                <option value="rating">Star Rating</option>
                <option value="text">Text</option>
              </select>
              <button
                @click="addQuestion"
                type="button"
                class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Add Question
              </button>
              <p v-if="errors.questions" class="mt-1 text-sm text-red-600">
                {{ errors.questions }}
              </p>
            </div>
          </div>
        </form>
      </div>

      <div
        class="flex justify-end space-x-3 mt-4 pt-4 border-t border-gray-200"
      >
        <button
          type="button"
          @click="$emit('close')"
          class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          @click="submitSurvey"
          class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {{ isEditing ? "Update" : "Create" }} Survey
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { surveyTemplateApi } from "@/api/surveyTemplateApi";
import { questionTemplateApi } from "@/api/questionTemplateApi";
import { mapState, mapActions } from "vuex";

export default {
  name: "SurveyBuilder",
  props: {
    isEditing: {
      type: Boolean,
      default: false,
    },
    initialSurvey: {
      type: Object,
      default: () => ({
        id: "",
        title: "",
        description: "",
        surveyMode: "adhoc",
        recurringPattern: "",
        type: "",
        targetModule: [],
        option: "",
        department: "",
        startDate: "",
        endDate: "",
        questions: [],
      }),
    },
    availableDepartments: {
      type: Array,
      required: true,
    },
    availableModules: {
      type: Array,
      required: true,
    },
    isSuperAdmin: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      survey: {
        ...this.initialSurvey,
        surveyMode: this.initialSurvey.surveyMode || "adhoc",
        type:
          this.initialSurvey.type ||
          (this.isSuperAdmin ? "" : "School/Department"),
        targetModule: this.initialSurvey.targetModule || [],
      },
      newQuestionType: "single",
      activeTab: "Survey Details",
      errors: {
        title: "",
        surveyMode: "",
        recurringPattern: "",
        type: "",
        targetModule: "",
        option: "",
        department: "",
        questions: "",
      },
      search: "",
      selectAllModules: false,
      surveyTemplates: [],
      questionTemplates: [],
      selectedSurveyTemplate: null,
      selectedQuestionTemplate: null,
      surveyTemplateSearch: "",
      questionTemplateSearch: "",
      surveyTemplateDropdownActive: false,
      questionTemplateDropdownActive: false,
    };
  },
  computed: {
    ...mapState({
      schools: (state) => state.schools,
      departments: (state) => state.departments,
    }),
    filteredSurveyTemplates() {
      return this.surveyTemplates.filter((template) =>
        template.name
          .toLowerCase()
          .includes(this.surveyTemplateSearch.toLowerCase())
      );
    },
    filteredQuestionTemplates() {
      return this.questionTemplates.filter((template) =>
        template.name
          .toLowerCase()
          .includes(this.questionTemplateSearch.toLowerCase())
      );
    },
    filteredModules() {
      if (!this.survey.department) return [];
      else {
        let _m2 = [];
        if (this.departments) {
          let school = this.schools.find(
            (x) => x.fullName == this.survey.department
          );
          if (school) {
            let departments = this.departments.filter(
              (x) => x.school == school.shortName
            );
            _m2 = this.availableModules.filter(
              (module) =>
                departments.find((x) => x.fullName == module.department) != null
            );
          }
        }
        let _m1 = this.availableModules.filter(
          (module) => module.department === this.survey.department
        );
        _m1 = _m1.concat(_m2);
        if (this.search)
          _m1 = _m1.filter(module=>module.moduleCode.toUpperCase().indexOf(this.search.toUpperCase())!=-1 
        || module.name.toUpperCase().indexOf(this.search.toUpperCase())!=-1)
        return _m1;
      }
    },
  },
  watch: {
    "survey.targetModule": {
      handler(newVal) {
        //const departmentModules = this.availableModules.filter(
        //  (module) => module.department === this.survey.department
        //);
        const departmentModules = this.filteredModules;
        this.selectAllModules = newVal.length === departmentModules.length;
      },
      deep: true,
    },
  },
  created() {
    if (this.initialSurvey.startDate) {
      const startDate = new Date(this.initialSurvey.startDate);
      startDate.setHours(startDate.getHours() + 8);
      this.survey.startDate = this.formatDateForInput(startDate);
    }
    if (this.initialSurvey.endDate) {
      const endDate = new Date(this.initialSurvey.endDate);
      endDate.setHours(endDate.getHours() + 8);
      this.survey.endDate = this.formatDateForInput(endDate);
    }
  },
  mounted() {
    this.fetchSurveyTemplates();
    this.fetchQuestionTemplates();
    if (!this.schools || this.schools.length == 0)
      this.$store.dispatch("fetchSchools");
    if (!this.departments || this.department.length == 0)
      this.$store.dispatch("fetchDepartments");

    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    async fetchSurveyTemplates() {
      try {
        this.surveyTemplates = await surveyTemplateApi.getAll();
      } catch (error) {
        console.error("Error fetching survey templates:", error);
      }
    },
    async fetchQuestionTemplates() {
      try {
        this.questionTemplates = await questionTemplateApi.getAll();
      } catch (error) {
        console.error("Error fetching question templates:", error);
      }
    },
    selectSurveyTemplate(template) {
      this.survey.questions = JSON.parse(JSON.stringify(template.questions));
      this.survey.questions.forEach((question) => {
        question.isMandatory = true;
        if (this.survey.type == 'Unveristy')
          question.option = 'module/staff';
        else
          question.option = 'module';
      });
      this.surveyTemplateDropdownActive = false;
      this.surveyTemplateSearch = "";
    },
    selectQuestionTemplate(template) {
      const newQuestion = {
        ...template,
        description: template.description || "",
      };
      //newQuestion.option = 'module';
      
      if (this.survey.type == 'Unveristy')
        newQuestion.option = 'module/staff';
        else
        newQuestion.option = 'module';
      
      newQuestion.isMandatory = true;
      this.survey.questions.push(newQuestion);
      this.questionTemplateSearch = "";
      this.questionTemplateDropdownActive = false;
    },
    addQuestion() {
      const newQuestion = {
        text: "",
        description: "",
        type: this.newQuestionType,
        options:
          this.newQuestionType === "single" ||
          this.newQuestionType === "multiple"
            ? [""]
            : [],
        maxRating: this.newQuestionType === "rating" ? 5 : null,
        option: this.survey.type === "School/Department" ? "" : undefined,
      };
      this.survey.questions.push(newQuestion);
    },
    getModuleCode(moduleId) {
      const module = this.availableModules.find((m) => m.id === moduleId);
      return module ? `${module.moduleCode} - ${module.name}` : "";
    },
    removeQuestion(index) {
      this.survey.questions.splice(index, 1);
    },
    addOption(question) {
      question.options.push("");
    },
    removeOption(question, index) {
      question.options.splice(index, 1);
    },
    moveQuestionUp(index) {
      if (index > 0) {
        const question = this.survey.questions.splice(index, 1)[0];
        this.survey.questions.splice(index - 1, 0, question);
      }
    },
    moveQuestionDown(index) {
      if (index < this.survey.questions.length - 1) {
        const question = this.survey.questions.splice(index, 1)[0];
        this.survey.questions.splice(index + 1, 0, question);
      }
    },
    submitSurvey() {
      this.errors = {
        title: "",
        surveyMode: "",
        recurringPattern: "",
        type: "",
        targetModule: "",
        option: "",
        department: "",
        questions: "",
      };

      if (!this.survey.title.trim()) {
        this.errors.title = "Title is required";
      }
      if (!this.survey.surveyMode) {
        this.errors.surveyMode = "Survey mode is required";
      }
      if (
        this.survey.surveyMode === "recurring" &&
        !this.survey.recurringPattern
      ) {
        this.errors.recurringPattern =
          "Recurring pattern is required for recurring surveys";
      }
      if (this.isSuperAdmin && !this.survey.type) {
        this.errors.type = "Type is required";
      }
      if (
        this.survey.type !== "University" &&
        this.survey.targetModule.length === 0
      ) {
        this.errors.targetModule = "At least one target module is required";
      }
      if (!this.survey.department) {
        this.errors.department = "Department is required";
      }
      if (this.survey.questions.length === 0) {
        this.errors.questions = "At least one question is required";
      }

      if (this.survey.type === "School/Department") {
        const invalidQuestions = this.survey.questions.filter((q) => !q.option);
        if (invalidQuestions.length > 0) {
          this.errors.questions =
            "All questions in a School/Department survey must have an option selected";
          this.activeTab = "Questions";
          return;
        }
      }

      if (Object.values(this.errors).some((error) => error !== "")) {
        this.activeTab =
          Object.keys(this.errors).find((key) => this.errors[key] !== "") ===
          "questions"
            ? "Questions"
            : "Survey Details";
        return;
      }

      this.$emit("submit", this.survey);
    },
    toggleAllModules() {
      if (this.selectAllModules) {
        //this.survey.targetModule = this.availableModules
        //  .filter((module) => module.department === this.survey.department)
        //  .map((module) => module.id);
        this.survey.targetModule = this.filteredModules.map(
          (module) => module.id
        );
      } else {
        this.survey.targetModule = [];
      }
    },
    handleDepartmentChange() {
      this.survey.targetModule = [];
      this.selectAllModules = false;
    },
    formatDateForInput(date) {
      if (!date) return "";
      return new Date(date).toISOString().slice(0, 16);
    },
    handleClickOutside(event) {
      const surveyTemplateDropdown = this.$refs.surveyTemplateDropdown;
      const questionTemplateDropdown = this.$refs.questionTemplateDropdown;
      const moduleDropdown = this.$el.querySelector("#department");

      if (moduleDropdown && !moduleDropdown.contains(event.target)) {
        this.showModuleDropdown = false;
      }

      if (
        surveyTemplateDropdown &&
        !surveyTemplateDropdown.contains(event.target)
      ) {
        this.surveyTemplateDropdownActive = false;
      }

      if (
        questionTemplateDropdown &&
        !questionTemplateDropdown.contains(event.target)
      ) {
        this.questionTemplateDropdownActive = false;
      }
    },
  },
};
</script>